import React, { useState, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import { Flex, Button } from "antd"
import { DeleteOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Modal } from "ui/Modal"
import { ConfirmModal } from "ui/ConfirmModal"
import SeasonForm from "./components/SeasonForm"
import styles from "./index.module.css"

/**
 * Unified AvailabilityModal component for adding and editing crew member availability
 */
const AvailabilityModal = ({ open, onCancel, onSave, mode = "add", initialData = null }) => {
  const { t } = useTranslation()
  const scrollRef = useRef(null)

  // Initialize seasons based on mode
  const getInitialSeasons = () => {
    if (mode === "edit" && initialData) {
      return initialData
    }
    return [{ id: 1, number: 1 }]
  }

  const [seasons, setSeasons] = useState(getInitialSeasons())
  const initialSeasonsRef = useRef(getInitialSeasons())
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [seasonToDelete, setSeasonToDelete] = useState(null)

  // Update seasons when modal opens with new data
  useEffect(() => {
    if (open) {
      const newSeasons = getInitialSeasons()
      setSeasons(newSeasons)
      initialSeasonsRef.current = newSeasons
    }
  }, [open, mode, initialData])

  const handleAddSeason = () => {
    const newSeasonNumber = seasons.length + 1
    setSeasons([...seasons, { id: Date.now(), number: newSeasonNumber }])

    // Scroll to bottom after new season is rendered
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight
      }
    }, 100)
  }

  const handleDeleteClick = (seasonId, seasonNumber) => {
    // Ensure at least one season always exists
    if (seasons.length <= 1) {
      return
    }

    setSeasonToDelete({ id: seasonId, number: seasonNumber })
    setIsDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (!seasonToDelete) return

    const updatedSeasons = seasons.filter((season) => season.id !== seasonToDelete.id)

    // Renumber remaining seasons
    const renumberedSeasons = updatedSeasons.map((season, index) => ({
      ...season,
      number: index + 1
    }))

    setSeasons(renumberedSeasons)
    setIsDeleteConfirmOpen(false)
    setSeasonToDelete(null)
  }

  const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false)
    setSeasonToDelete(null)
  }

  const handleSaveChanges = () => {
    // TODO: Collect all season data and call onSave with appropriate data
    console.log(`${mode === "edit" ? "Update" : "Save"} availability changes`)
    onSave()
  }

  const handleCancel = () => {
    if (mode === "add") {
      // Reset to initial blank state for add mode
      setSeasons([{ id: 1, number: 1 }])
    } else {
      // Revert to original data for edit mode
      setSeasons(initialSeasonsRef.current)
    }
    onCancel()
  }

  const modalTitle = mode === "edit"
    ? t("crewMember.availability.editTitle")
    : t("crewMember.availability.addTitle")

  return (
    <>
      <Modal
        open={open}
        onCancel={handleCancel}
        title={modalTitle}
        width={800}
        className={styles.modal}
        centered
        footer={null}
      >
        <Flex vertical className={styles.modalContainer}>
          {/* Scrollable Content */}
          <Flex vertical gap={24} className={styles.modalContent} ref={scrollRef}>
            {/* Season Forms */}
            <Flex vertical gap={32}>
              {seasons.map((season, index) => {
                const isLastSeason = index === seasons.length - 1

                return (
                  <Flex key={season.id} vertical gap={16}>
                    {/* Season Header with Delete Button */}
                    {seasons.length > 1 && (
                      <Flex justify="space-between" align="center">
                        <div className={styles.seasonHeader}>
                          <span className={styles.seasonTitle}>
                            {t("crewMember.availability.season", { number: season.number })}
                          </span>
                        </div>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteClick(season.id, season.number)}
                          className={styles.deleteSeasonButton}
                        >
                          {t("crewMember.availability.buttons.deleteSeason")}
                        </Button>
                      </Flex>
                    )}

                    <div className={isLastSeason ? "" : styles.seasonFormWrapper}>
                      <SeasonForm
                        seasonNumber={season.number}
                        showSeasonTitle={false}
                        seasonData={season.data}
                      />
                    </div>
                  </Flex>
                )
              })}
            </Flex>
          </Flex>

          {/* Fixed Bottom Section */}
          <Flex vertical className={styles.fixedFooter}>
            {/* Add Seasonal Availability Button */}
            <Button
              type="link"
              onClick={handleAddSeason}
              className={styles.addSeasonButton}
            >
              + {t("crewMember.availability.buttons.addSeasonalAvailability")}
            </Button>

            {/* Footer Buttons */}
            <Flex gap={12} justify="flex-start" className={styles.modalFooter}>
              <Button
                onClick={handleCancel}
                variant="outlined"
                color="primary"
                size="large"
                className={styles.cancelButton}
              >
                {t("crewMember.availability.buttons.cancel")}
              </Button>
              <Button
                type="primary"
                onClick={handleSaveChanges}
                size="large"
                className={styles.saveButton}
              >
                {t("crewMember.availability.buttons.saveChanges")}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={isDeleteConfirmOpen}
        title={t("crewMember.availability.deleteConfirmation.title")}
        info={t("crewMember.availability.deleteConfirmation.message", {
          seasonNumber: seasonToDelete?.number || ""
        })}
        onCancel={handleCancelDelete}
        onDelete={handleConfirmDelete}
        cancelLabel={t("crewMember.availability.buttons.cancel")}
        deleteLabel={t("crewMember.availability.buttons.delete")}
      />
    </>
  )
}

AvailabilityModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["add", "edit"]),
  initialData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      number: PropTypes.number.isRequired,
      data: PropTypes.object
    })
  )
}

export default AvailabilityModal
